import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminTrilhaService, catalogService } from '../../shared/services';
import type { Curso } from '../../shared/types';
import { Save, X, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

export function AdminTrilhaFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    titulo: '',
    slug: '',
    descricao: '',
    nivel: 'iniciante' as 'iniciante' | 'intermediario' | 'avancado',
    ativa: true,
    capaUrl: '',
    cursosIds: [] as string[],
  });

  const { data: trilha } = useQuery({
    queryKey: ['admin-trilha', id],
    queryFn: () => adminTrilhaService.getTrilha(id!),
    enabled: isEditing,
  });

  const { data: cursosData } = useQuery({
    queryKey: ['cursos-all'],
    queryFn: () => catalogService.getCursos({ page: 1 }),
  });

  useEffect(() => {
    if (trilha) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      setFormData({
        titulo: trilha.titulo,
        slug: trilha.slug,
        descricao: trilha.descricao,
        nivel: trilha.nivel,
        ativa: trilha.ativa,
        capaUrl: trilha.capaUrl || '',
        cursosIds: trilha.cursos?.map((c: Curso) => c.id) || [],
      });
    }
  }, [trilha]);

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => adminTrilhaService.createTrilha(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-trilhas'] });
      navigate('/admin/trilhas');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: typeof formData) => adminTrilhaService.updateTrilha(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-trilhas'] });
      queryClient.invalidateQueries({ queryKey: ['admin-trilha', id] });
      navigate('/admin/trilhas');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isEditing) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleTituloChange = (titulo: string) => {
    setFormData(prev => ({
      ...prev,
      titulo,
      slug: titulo
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, ''),
    }));
  };

  const toggleCurso = (cursoId: string) => {
    setFormData(prev => ({
      ...prev,
      cursosIds: prev.cursosIds.includes(cursoId)
        ? prev.cursosIds.filter(id => id !== cursoId)
        : [...prev.cursosIds, cursoId],
    }));
  };

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link
            to="/admin/trilhas"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para trilhas
          </Link>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEditing ? 'Editar Trilha' : 'Nova Trilha'}
          </h1>
          <p className="text-gray-600 mt-2">
            {isEditing 
              ? 'Atualize as informações da trilha'
              : 'Preencha os dados para criar uma nova trilha de aprendizado'}
          </p>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Informações Básicas */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-6">Informações Básicas</h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Título *
                </label>
                <input
                  type="text"
                  required
                  value={formData.titulo}
                  onChange={(e) => handleTituloChange(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: Trilha de Excel Avançado"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slug *
                </label>
                <input
                  type="text"
                  required
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="trilha-excel-avancado"
                />
                <p className="text-sm text-gray-500 mt-1">
                  URL amigável da trilha (gerado automaticamente a partir do título)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição *
                </label>
                <textarea
                  required
                  rows={4}
                  value={formData.descricao}
                  onChange={(e) => setFormData(prev => ({ ...prev, descricao: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Descreva o objetivo e conteúdo da trilha..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nível *
                  </label>
                  <select
                    required
                    value={formData.nivel}
                    onChange={(e) => setFormData(prev => ({ ...prev, nivel: e.target.value as 'iniciante' | 'intermediario' | 'avancado' }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="iniciante">Iniciante</option>
                    <option value="intermediario">Intermediário</option>
                    <option value="avancado">Avançado</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL da Capa
                  </label>
                  <input
                    type="url"
                    value={formData.capaUrl}
                    onChange={(e) => setFormData(prev => ({ ...prev, capaUrl: e.target.value }))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://..."
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.ativa}
                    onChange={(e) => setFormData(prev => ({ ...prev, ativa: e.target.checked }))}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div>
                    <p className="font-medium text-gray-900">Trilha Ativa</p>
                    <p className="text-sm text-gray-600">
                      Desmarque para ocultar a trilha dos alunos
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Cursos da Trilha */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Cursos da Trilha</h2>
            <p className="text-sm text-gray-600 mb-6">
              Selecione os cursos que farão parte desta trilha
            </p>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {cursosData && Array.isArray(cursosData) && cursosData.map((curso: Curso) => (
                <label
                  key={curso.id}
                  className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={formData.cursosIds.includes(curso.id)}
                    onChange={() => toggleCurso(curso.id)}
                    className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{curso.titulo}</p>
                    <p className="text-sm text-gray-600 line-clamp-2">{curso.descricao}</p>
                    <div className="flex gap-2 mt-2">
                      <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded">
                        {curso.categoria}
                      </span>
                      {curso.duracao && (
                        <span className="text-xs px-2 py-1 bg-gray-100 text-gray-800 rounded">
                          {curso.duracao}
                        </span>
                      )}
                    </div>
                  </div>
                </label>
              ))}

              {cursosData && Array.isArray(cursosData) && cursosData.length === 0 && (
                <p className="text-center text-gray-500 py-8">
                  Nenhum curso disponível
                </p>
              )}
            </div>

            {formData.cursosIds.length > 0 && (
              <p className="text-sm text-blue-600 mt-4">
                {formData.cursosIds.length} curso(s) selecionado(s)
              </p>
            )}
          </div>

          {/* Ações */}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save className="w-5 h-5" />
              {isEditing ? 'Salvar Alterações' : 'Criar Trilha'}
            </button>
            <Link
              to="/admin/trilhas"
              className="flex items-center gap-2 px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <X className="w-5 h-5" />
              Cancelar
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
